import React from 'react';
import styled from 'styled-components';
import { jlMoneyColors } from '../constants/colors';

interface StepProps {
  status: 'completed' | 'upcoming' | 'current';
  totalSteps: number;
  index: number;
  step: string;
}

interface ProgressBarProps {
  currentStep: number;
  className?: string;
  steps: string[];
}

const StepContainer = styled.div<{ totalSteps: number; index: number }>`
  position: absolute;
  left: ${({ totalSteps, index }) => `${(index / (totalSteps - 1)) * 100}%`};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: visible;
  width: 0; // This ensures the container doesn't affect layout

  @media (max-width: 600px) {
    left: ${({ totalSteps, index }) => `${(index / (totalSteps - 1)) * 100}%`};
  }
`;

const StepCircle = styled.circle<{ status: string }>`
  fill: ${({ status }) => (status === 'current' ? jlMoneyColors.jlMoneyGreenDark : '#ffffff')};
  stroke: ${({ status }) => (status === 'completed' || status === 'current' ? '#2d3142' : '#A9A9A9')};

  @media (max-width: 600px) {
    r: ${({ status }) => (status === 'current' ? '18' : '12')};
  }
`;

const StepText = styled.text<{ status: string }>`
  fill: ${({ status }) => (status === 'current' ? '#DFFF7B' : '#000000')};

  @media (max-width: 600px) {
    display: ${({ status }) => (status === 'upcoming' ? 'none' : 'block')};
  }
`;

const StepLabel = styled.div<{ status: string }>`
  margin-top: 8px;
  font-size: 16px;
  color: #2d3142;
  white-space: nowrap;

  @media (max-width: 600px) {
    display: ${({ status }) => (status === 'current' ? 'block' : 'none')};
  }
`;

const StepSVG = styled.svg<{ diameter: number; viewBox: string }>`
  width: ${({ diameter }) => `${diameter}px`};
  height: ${({ diameter }) => `${diameter}px`};
  viewbox: ${({ viewBox }) => viewBox};
  overflow: unset;
`;

const ProgressBarContainer = styled.div`
  --progress-bar-height: 66px;
  --progress-bar-max-width: 612px;
  --progress-bar-small-max-width: 327px;

  position: relative;
  height: var(--progress-bar-height);
  margin: 20px auto;
  width: calc(var(--progress-bar-max-width) - 64px);

  @media (max-width: 600px) {
    width: calc(var(--progress-bar-small-max-width) - 64px);
    margin-right: 0;
    margin-left: 0;
  }
`;

const ProgressBarLine = styled.div`
  --progress-bar-line-height: 2px;
  --progress-bar-line-color: #d3d3d3;

  position: absolute;
  top: 15px;
  left: 0;
  right: 0;
  height: var(--progress-bar-line-height);
  background-color: var(--progress-bar-line-color);
  z-index: -1;
`;

const Step = ({ totalSteps, status, index, step }: StepProps) => {
  const radius = 18;
  const strokeWidth = 1;
  const diameter = radius * 2;
  const viewBox = `0 0 ${diameter} ${diameter}`;

  return (
    <StepContainer totalSteps={totalSteps} index={index}>
      <StepSVG diameter={diameter} viewBox={viewBox}>
        <StepCircle r={radius - strokeWidth / 2} strokeWidth={strokeWidth} status={status} cx={radius} cy={radius} />
        {status === 'completed' ? (
          <path
            stroke={jlMoneyColors.jlMoneyGreenDark}
            strokeLinejoin="round"
            strokeLinecap="round"
            d="M12 18l4 4 8-8"
            strokeWidth="2"
            fill="none"
          />
        ) : (
          <StepText textAnchor="middle" fontWeight="500" status={status} fontSize={16} dy=".3em" x="50%" y="50%">
            {index + 1}
          </StepText>
        )}
      </StepSVG>
      <StepLabel status={status}>{step}</StepLabel>
    </StepContainer>
  );
};

export const JohnLewisProgressBar = ({ currentStep, className, steps }: ProgressBarProps) => (
  <ProgressBarContainer className={className}>
    {steps.map((step, index) => (
      <React.Fragment key={index}>
        <Step
          status={index < currentStep ? 'completed' : index === currentStep ? 'current' : 'upcoming'}
          totalSteps={steps.length}
          index={index}
          step={step}
        />
      </React.Fragment>
    ))}
    <ProgressBarLine />
  </ProgressBarContainer>
);
